/**
 * The Spotlight stage — prototype H's left half: kicker, the one item huge and
 * quiet, the "serves →" line, the then-line. Clock small, Mode toggle
 * bottom-right. This is the loudest element on the board and the only one
 * allowed to be loud.
 */

import type { BoardStateData } from '../types';
import type { Spotlight } from '../spotlight';
import { nextEvent } from '../spotlight';
import { sourceStatus, type SourceKey } from '../freshness';
import { esc, hhmm } from './util';

export interface StageOptions {
  spotlight: Spotlight;
  board: BoardStateData;
  now: Date;
  modeOn: boolean;
  modePending: boolean;
  /** the compose sheet is unavailable on this surface (phone cut). */
  canCompose: boolean;
}

export function renderStage(o: StageOptions): string {
  const { board, spotlight, now } = o;
  const ev = nextEvent(board.calendar.data.events ?? [], now);
  const reviews = board.state.data?.reviews;
  const thenParts: string[] = [];
  thenParts.push(ev ? `then · ${hhmm(ev.start)} ${esc(ev.title)}` : 'then · nothing else scheduled');
  if (reviews?.weekly) {
    thenParts.push(
      reviews.weekly.overdue
        ? 'weekly review overdue'
        : `weekly review ${dueIn(reviews.weekly.dueOn, board.today)}`,
    );
  }

  const clock = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

  const composeCta =
    spotlight.source === 'compose'
      ? o.canCompose
        ? `<button class="cta" data-action="open-compose">Compose today</button>`
        : `<div class="cta-note">Composing is a sit-down act — do it on the monitor or laptop.</div>`
      : '';

  return `<div class="stage">
    <div class="k">${esc(spotlight.kicker)}</div>
    <div class="what">${esc(spotlight.what)}</div>
    <div class="serves">${esc(spotlight.serves)}</div>
    <div class="after">${thenParts.join(' · ')}</div>
    ${composeCta}
    <div class="clock num">${clock}</div>
    ${renderModeButton(o.modeOn, o.modePending)}
    ${renderSources(board, now)}
  </div>`;
}

export function renderModeButton(on: boolean, pending: boolean): string {
  return `<button class="mode-btn ${on ? 'on' : ''} ${pending ? 'pending' : ''}" data-action="toggle-mode" aria-pressed="${on}">${
    on ? 'Mode on · end' : 'Mode · focus'
  }</button>`;
}

const SOURCE_LABELS: Array<{ key: SourceKey; label: string; pick: (b: BoardStateData) => unknown }> = [
  { key: 'defs', label: 'defs', pick: (b) => b.defs },
  { key: 'calendar', label: 'cal', pick: (b) => b.calendar },
  { key: 'reminders', label: 'clickup', pick: (b) => b.reminders },
  { key: 'state', label: 'state', pick: (b) => b.state },
];

/**
 * Source icons (freshness ticket): grey when the envelope is not ok or is
 * older than ~2x its cadence, and carry the "as of HH:MM" mark. Never blank,
 * never silently wrong.
 */
export function renderSources(board: BoardStateData, now: Date): string {
  const dots = SOURCE_LABELS.map(({ key, label, pick }) => {
    const st = sourceStatus(key, pick(board) as never, now);
    const cls = st.healthy ? 'src' : st.ok ? 'src stale' : 'src down';
    const title = st.healthy ? `${label} · fresh` : `${label} · as of ${st.asOf}${st.ok ? ' (stale)' : ' (last refresh failed)'}`;
    return `<span class="${cls}" title="${esc(title)}"><i></i>${label}${st.healthy ? '' : ` <span class="asof">${esc(st.asOf)}</span>`}</span>`;
  }).join('');
  return `<div class="sources">${dots}</div>`;
}

function dueIn(dueOn: string, today: string): string {
  const days = Math.round((Date.parse(`${dueOn}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
  if (!Number.isFinite(days)) return '—';
  if (days === 0) return 'due today';
  if (days < 0) return `${-days}d late`;
  return `in ${days}d`;
}
